import {
    Badge,
    BlockStack,
    Card,
    Form,
    Page,
    TextField,
    Button,
    Checkbox,
    InlineStack,
    Toast,
    Banner,
} from "@shopify/polaris";
import { useNavigate } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { useState } from "react";
import { AlertTriangleIcon, LockIcon } from "@shopify/polaris-icons";

import { MultiSelectCombobox } from "../components/atoms/MultiSelectCombobox";
import {
    useCreateExportRuleMutation,
    useGetStaffUsersQuery,
} from "../store/store/slices/ExportRTK";
import { EXPORT_FIELD_MAPPING } from "../utils";

// Column options
const exportColumns = Object.keys(EXPORT_FIELD_MAPPING);

type FormValues = {
    ruleName: string;
    columns: string[];
    staff: string[];
    emailReceivers: string[];
    notifyAdmin: boolean;
    exportRangeDays: number
};

export default function ExportRulesCreate() {
    const navigate = useNavigate();
    const [toastMessage, setToastMessage] = useState<{
        msg: string,
        error: boolean
    } | null>(null);

    // Fetch staff users
    const { data: staffData, isLoading: isStaffLoading } = useGetStaffUsersQuery();
    const staffOptions =
        staffData?.data?.map((user: any) => ({
            label: `${user.first_name} ${user.last_name}`,
            value: user._id, // send _id
        })) || [];

    // Build a map for showing names on tags
    const staffIdToName = staffOptions.reduce((acc, curr) => {
        acc[curr.value] = curr.label;
        return acc;
    }, {} as Record<string, string>);

    const [createExportRule, { isLoading }] = useCreateExportRuleMutation();

    const { control, handleSubmit } = useForm<FormValues>({
        defaultValues: {
            ruleName: "",
            columns: [],
            staff: [],
            emailReceivers: [],
            notifyAdmin: true,
        },
    });

    const showToast = (message: string, error: boolean = true) => setToastMessage({ msg: message, error });

    const onSubmit = async (data: FormValues) => {
        if (!data.ruleName.trim()) return showToast("Rule name is required");
        if (!data.columns.length) return showToast("At least one column is required");
        if (!data.staff.length) return showToast("At least one staff user is required");
        if (!data.exportRangeDays) return showToast("Export days range is required");

        try {
            const mappedColumns = data.columns.map(
                (col) => EXPORT_FIELD_MAPPING[col]
            );

            const payload = {
                name: data.ruleName,
                columns: mappedColumns, // ✅ send DB fields
                users: data.staff,
                emailReceivers: data.emailReceivers,
                notifyAdmin: data.notifyAdmin,
                exportRangeDays: data.exportRangeDays,
            };

            const res = await createExportRule(payload).unwrap();
            showToast("Export rule created successfully 🎉", false);

            setTimeout(() => {
                navigate("/dashboard/export-rules");
            }, 1000);
        } catch (error: any) {
           console.log({error})
        }
    };

    const toastMarkup = toastMessage ? (
        <Toast
            content={toastMessage.msg}
            error={toastMessage.error}
            onDismiss={() => setToastMessage(null)}
        />
    ) : null;

    return (
        <Form onSubmit={handleSubmit(onSubmit)}>
            <Page
                narrowWidth
                title="Export rules"
                backAction={{ onAction: () => navigate(-1) }}
                titleMetadata={
                    <Badge tone="success-strong" icon={LockIcon}>
                        Admin only
                    </Badge>
                }
            >
                <Card>
                    <BlockStack gap="400">
                        <Badge tone="info" size="large" icon={AlertTriangleIcon}>
                            Exports are generated in CSV format. Only the selected order columns will be included. Selected staff can export data, and chosen users will receive email alerts when an export is triggered.
                        </Badge>
                        {/* Rule Name */}
                        <Controller
                            name="ruleName"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    label="Rule name"
                                    autoComplete="off"
                                    value={field.value}
                                    onChange={field.onChange}
                                />
                            )}
                        />

                        <Controller
                            name="exportRangeDays"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    label="Number of days the staff can export data within"
                                    autoComplete="off"
                                    value={field.value}
                                    type="number"
                                    onChange={field.onChange}
                                />
                            )}
                        />

                        {/* Columns */}
                        <Controller
                            name="columns"
                            control={control}
                            render={({ field }) => (
                                <MultiSelectCombobox
                                    options={exportColumns.map((col) => ({
                                        label: col,
                                        value: col,
                                    }))}
                                    selected={field.value}
                                    onChange={field.onChange}
                                    label="Select columns that can be exported as order data"
                                    placeholder="Search columns which can be exported"
                                />
                            )}
                        />

                        {/* Staff Users */}
                        <Controller
                            name="staff"
                            control={control}
                            render={({ field }) => (
                                <MultiSelectCombobox
                                    options={staffOptions}
                                    selected={field.value}
                                    onChange={field.onChange}
                                    label="Select users who can export the above following columns"
                                    placeholder={
                                        isStaffLoading
                                            ? "Loading staff..."
                                            : "Selected users can export orders"
                                    }
                                    tagRenderer={(selectedValue: string) => staffIdToName[selectedValue]} // <-- Show name instead of _id
                                />
                            )}
                        />
                        {/* Staff Users */}
                        <Controller
                            name="emailReceivers"
                            control={control}
                            render={({ field }) => (
                                <MultiSelectCombobox
                                    options={staffOptions}
                                    selected={field.value}
                                    onChange={field.onChange}
                                    label="Select who can receive export alert email"
                                    placeholder={
                                        isStaffLoading
                                            ? "Loading users..."
                                            : "Selected users will get email"
                                    }
                                    tagRenderer={(selectedValue: string) => staffIdToName[selectedValue]} // <-- Show name instead of _id
                                />
                            )}
                        />
                        {/* Notify Admin */}
                        <Controller
                            name="notifyAdmin"
                            control={control}
                            render={({ field }) => (
                                <Checkbox
                                    label={
                                        <InlineStack gap="300">
                                            Notify admin
                                            <Badge icon={AlertTriangleIcon} tone="success">
                                                Admin will get an email when user exports orders
                                            </Badge>
                                        </InlineStack>
                                    }
                                    checked={field.value}
                                    onChange={field.onChange}
                                />
                            )}
                        />
                        {/* Save Button */}
                        <div style={{ display: "flex", justifyContent: "end" }}>
                            <Button size="slim" submit variant="primary" loading={isLoading}>
                                Save Rule
                            </Button>
                        </div>
                    </BlockStack>
                </Card>
            </Page>
            {toastMarkup}
        </Form>
    );
}